import { ApiFetchError, BaseError, UnimplementedError } from '@/common/errors';
import {
  BaseHazardEntity,
  CentreEntity,
  CentreSubType,
  ClosureEntity,
  ClosureSubType,
  EnrichedWarningEntity,
  EntitySubType,
  EventEntity,
  GetCentresModel,
  GetClosuresModel,
  GetEventsModel,
  GetHazardsModel,
  GetIncidentsModel,
  GetWarningByIdModel,
  GetWarningsModel,
  HAZARD_MARKERS,
  HazardEntity,
  HazardPriorityMapping,
  HazardType,
  HazardsDataSource,
  HazardsRepository,
  IconName,
  IncidentEntity,
  IncidentSubType,
  OtherWarningSubType,
  S3HazardsDataSource,
  WarningEntity,
  WarningSubType,
  WarningSubTypes,
  centresToSchemeMap,
  closuresToSchemeMap,
  incidentsToSchemeMap,
  warningsToSchemeMap,
} from '@/features/hazards';

import { Either, left, right } from 'fp-ts/lib/Either';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { DateTime } from 'luxon';

import { captureException } from '@sentry/vue';

export class HazardsRepositoryImpl implements HazardsRepository {
  dataSource: HazardsDataSource;
  s3DataSource: S3HazardsDataSource;

  constructor(dataSource: HazardsDataSource, s3DataSource: S3HazardsDataSource) {
    this.dataSource = dataSource;
    this.s3DataSource = s3DataSource;
  }

  /**
   * @deprecated - The getEvents method should be used instead to get the event details.
   * @param hazardType
   * @param hazardId
   * @returns
   */
  async getHazardById(hazardType: HazardType, hazardId: string): Promise<Either<BaseError, EnrichedWarningEntity>> {
    try {
      let hazardModel: GetWarningByIdModel | undefined;

      switch (hazardType) {
        case HazardType.Warning:
          hazardModel = await this.dataSource.getWarningById(hazardId);
          break;
        default:
          throw new UnimplementedError(`get ${hazardType} by id`);
      }

      return right(hazardModel);
    } catch (e) {
      captureException(e);

      if (e instanceof BaseError) {
        return left(e);
      }

      return left(new ApiFetchError(`${hazardType} - ${hazardId}`));
    }
  }

  async getHazards(hazardType: HazardType, language?: string): Promise<Either<BaseError, HazardEntity[]>> {
    try {
      let hazardModels: GetHazardsModel[] = [];

      switch (hazardType) {
        case HazardType.Incident:
          hazardModels = await this.dataSource.getIncidents(language);
          break;
        case HazardType.Warning:
          hazardModels = await this.dataSource.getWarnings(language);
          break;
        case HazardType.Centre:
          hazardModels = await this.dataSource.getCentres(language);
          break;
        case HazardType.Closure:
          hazardModels = await this.dataSource.getClosures(language);
          break;
        case HazardType.Event:
          hazardModels = await this.dataSource.getEvents(language);
          break;
        default:
          throw new UnimplementedError(`get ${hazardType}`);
      }

      return right(this.mapModelsToEntities({ hazardModels, hazardType }));
    } catch (e) {
      captureException(e);
      return left(new ApiFetchError(hazardType));
    }
  }

  // API Models
  mapModelsToEntities({ hazardModels, hazardType }: { hazardModels: GetHazardsModel[]; hazardType: HazardType }): HazardEntity[] {
    switch (hazardType) {
      case HazardType.Warning:
        return hazardModels.map((model) => this.mapModelToEntity(model));
      case HazardType.Incident:
      case HazardType.Centre:
      case HazardType.Closure:
      case HazardType.Event:
        return this.sortHazardEntities(hazardModels.map((model) => this.mapModelToEntity(model)));
      default:
        throw new UnimplementedError(`get ${hazardType}`);
    }
  }

  mapModelToEntity(hazardModel: GetHazardsModel): HazardEntity {
    const geoSource = hazardModel?.['geo-source'] as FeatureCollection;
    const geoSourceFeatures = geoSource?.['features'] ?? [];

    const points = geoSourceFeatures.filter((feature) => feature.geometry.type === 'Point');
    const pointFeatures = this.enrichFeatures(points, hazardModel);

    const polylines = geoSourceFeatures.filter(
      (feature) => feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString'
    );
    const polylineFeatures = this.enrichFeatures(polylines, hazardModel);

    const polygons = geoSourceFeatures.filter((feature) => feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon');
    const polygonFeatures = this.enrichFeatures(polygons, hazardModel);

    const baseHazardEntity: BaseHazardEntity = {
      id: hazardModel.id!,
      entityType: hazardModel.entityType as HazardType,
      entitySubType: hazardModel.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType],

      eventId: hazardModel.event!,

      title: hazardModel.name,
      publishedAt: hazardModel['published-date-time'],

      polygonFeatures,
      polylineFeatures,
      pointFeatures,
      pointFeature: pointFeatures.at(0),
    };

    let partialHazardEntity: Partial<HazardEntity> = {};

    switch (hazardModel.entityType) {
      case HazardType.Warning:
        partialHazardEntity = this.mapWarningModelToPartialEntity(hazardModel as GetWarningsModel);
        break;
      case HazardType.Incident:
        partialHazardEntity = this.mapIncidentModelToPartialEntity(hazardModel as GetIncidentsModel);
        break;
      case HazardType.Centre:
        partialHazardEntity = this.mapCentreModelToPartialEntity(hazardModel as GetCentresModel);
        break;
      case HazardType.Closure:
        partialHazardEntity = this.mapClosureModelToPartialEntity(hazardModel as GetClosuresModel);
        break;
      case HazardType.Event:
        partialHazardEntity = this.mapEventModelToPartialEntity(hazardModel as GetEventsModel);
        break;

      default:
        break;
    }

    const hazardEntity: HazardEntity = { ...baseHazardEntity, ...(partialHazardEntity as HazardEntity) };

    return hazardEntity;
  }

  mapEventModelToPartialEntity(eventModel: GetEventsModel): Partial<EventEntity> {
    const banDate = eventModel['date-of-ban'];
    return {
      publishedAt: eventModel['published-date-time'],
      issuingAgency: eventModel['issuing-agency'],
      eventSummary: eventModel['web-message'],
      eventSummaryText: eventModel['web-text-message'],
      source: eventModel['source'] ?? 'internal',
      dateOfBan: banDate ? DateTime.fromISO(banDate) : undefined,
      banInformation: eventModel['ban-information'],
      rawEvent: eventModel,
    };
  }

  /**
   * Sort hazard entities by priority and, if needed, the published date.
   */
  sortHazardEntities(hazardEntities: HazardEntity[], hazardType?: HazardType): HazardEntity[] {
    return hazardEntities.sort((a, b) => {
      const aPriority = HazardPriorityMapping[a.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType]] ?? 0;
      const bPriority = HazardPriorityMapping[b.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType]] ?? 0;

      /**
       * This is to ensure warnings of the same type (Bushfire) are sorted by priority.
       * @example
       *  - x Emergency Warning comes before x Watch and Act Warning
       */
      if (Math.floor(aPriority) !== Math.floor(bPriority)) {
        return bPriority - aPriority;
      }

      /**
       * If the hazard type is Warning, we attempt to sort by the `sorting-priority` property.
       */

      if (hazardType === HazardType.Warning) {
        const aSortingPriority = (a as WarningEntity).sortingPriority ?? 0;
        const bSortingPriority = (b as WarningEntity).sortingPriority ?? 0;

        return aSortingPriority - bSortingPriority;
      }

      // Sort by published date if the priority is the same
      const aPublishedAt = a.publishedAt as string;
      const bPublishedAt = b.publishedAt as string;

      if (aPublishedAt && bPublishedAt) {
        const aMillis = DateTime.fromISO(aPublishedAt).toMillis();
        const bMillis = DateTime.fromISO(bPublishedAt).toMillis();

        return bMillis - aMillis;
      }

      /**
       * This is to ensure warnings of different types (Bushfire, Flood, etc) but the same alert level (Emergency, Watch and Act, etc)
       * are sorted by priority.
       * @example
       *  - Bushfire Emergency Warning comes before Flood Emergency Warning
       */
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }

      // Sort by title (alphabetically) if the published date is the same
      const aTitle = a.title;
      const bTitle = b.title;

      if (aTitle && bTitle) {
        return aTitle.localeCompare(bTitle);
      }

      return 0;
    });
  }

  mapWarningModelToPartialEntity(warningModel: GetWarningsModel): Partial<WarningEntity> {
    const entitySubType = warningModel['entitySubType'];
    const scheme = warningsToSchemeMap[entitySubType as WarningSubType];

    if (
      [
        WarningSubTypes.OtherWarningWarningEntity,
        WarningSubTypes.OtherWatchAndActWarningEntity,
        WarningSubTypes.OtherEmergencyWarningWarningEntity,
      ].includes(entitySubType as OtherWarningSubType) &&
      warningModel['warning-icon']
    ) {
      scheme.icon = HAZARD_MARKERS[warningModel['warning-icon'] as IconName] ?? scheme.icon;
    }

    return {
      ...scheme,
      description: this.composeWarningDescription(warningModel),
      warningType: warningModel['warning-type'],
      publishingStatus: warningModel['publishing-status'],
      geoSource: warningModel['geo-source'],
      alertLine: warningModel['alert-line'],
      safeToLeave: warningModel['safe-to-leave'],
      impactToHomesNote: warningModel['impact-to-homes-note'],
      telephoneWarning: warningModel['telephone-warning'],
      alertLevelNote: warningModel['alert-level-note'],
      threatToHomesNote: warningModel['threat-to-homes-note'],
      fireLocationNote: warningModel['fire-location-note'],
      fireDirectionNote: warningModel['fire-direction-note'],
      whatToDoNote: warningModel['what-to-do-note'],
      safestRouteNote: warningModel['safest-route-note'],
      sortingPriority: warningModel['sorting-priority'],
      actionStatement: warningModel['action-statement'],
    };
  }

  mapIncidentModelToPartialEntity(incidentModel: GetIncidentsModel): Partial<IncidentEntity> {
    const entitySubType = incidentModel['entitySubType'];
    const scheme = incidentsToSchemeMap[entitySubType as IncidentSubType];

    const incidentIcon = incidentModel['incident-icon'] as IconName | undefined;

    if (entitySubType === IncidentSubType.OtherIncidentEntity && incidentIcon) {
      scheme.icon = HAZARD_MARKERS[incidentIcon] ?? scheme?.icon;
    }

    return {
      ...scheme,
      lga: incidentModel['lga'],
      incidentType: incidentModel['incident-type'],
      isTransportation: incidentModel.isTransportation ?? false,
      transportationType: incidentModel.transportationType,
      isNaturalHazard: incidentModel.isNaturalHazard ?? false,
      reportedAt: incidentModel['start-date-time'],
      /**
       * The `updated-date-time` is used for both `publishedAt` and `updatedAt`.
       *
       * Some incidents don't have an `updated-date-time` and only have a `published-date-time`.
       * As such, we fallback to using the `published-date-time` if the `updated-date-time` is not available.
       */
      publishedAt: incidentModel['updated-date-time'] ?? incidentModel['published-date-time'],
      updatedAt: incidentModel['updated-date-time'] ?? incidentModel['published-date-time'],
      incidentStatus: incidentModel['incident-status'],
      description: this.composeIncidentDescription(incidentModel),
      'm511-description': incidentModel['description'],
      reportedNear: this.composeIncidentReportedNear(incidentModel),
      reportedIn: incidentModel['suburbs']?.at(0)?.toUpperCase(),
      'additional-details': incidentModel['additional-details'],
    };
  }

  mapClosureModelToPartialEntity(closureModel: GetClosuresModel): Partial<ClosureEntity> {
    const entitySubType = closureModel['entitySubType'];
    const scheme = closuresToSchemeMap[entitySubType as ClosureSubType];

    if (entitySubType === ClosureSubType.GenericClosureEntity && closureModel['closure-icon']) {
      scheme.icon = HAZARD_MARKERS[closureModel['closure-icon'] as IconName] ?? scheme.icon;
    }

    return {
      ...scheme,
      description: closureModel['headline'] ?? '',
      'conditions-and-advice-note': closureModel['conditions-and-advice-note'] ?? '',
      'description-note': closureModel['description-note'] ?? '',
      'started-at': closureModel['started-at'] ?? '',
      'last-updated-at': closureModel['last-updated-at'] ?? '',
      isTransportation: closureModel.isTransportation ?? false,
      transportationType: closureModel.transportationType,
    };
  }

  mapCentreModelToPartialEntity(centreModel: GetCentresModel): Partial<CentreEntity> {
    const entitySubType = centreModel['entitySubType'];
    const scheme = centresToSchemeMap[entitySubType as CentreSubType];

    if (entitySubType === CentreSubType.GenericCentreEntity && centreModel['centre-icon']) {
      scheme.icon = HAZARD_MARKERS[centreModel['centre-icon'] as IconName] ?? scheme.icon;
    }

    return {
      ...scheme,
      description: centreModel['headline'] ?? '',
    };
  }

  enrichFeatures(features: Feature<Geometry, GeoJsonProperties>[], hazardModel: GetHazardsModel) {
    return features.map((feature, index) => this.enrichFeature(feature, hazardModel, index));
  }

  enrichFeature(
    feature: Feature<Geometry, GeoJsonProperties>,
    hazardModel: GetHazardsModel,
    index?: number
  ): Feature<Geometry, GeoJsonProperties> {
    const updatedAt = DateTime.fromISO(hazardModel['published-date-time'] ?? hazardModel['updated-date-time'] ?? '');

    return {
      ...feature,
      // TODO: Type the properties object
      properties: {
        ...feature.properties,
        id: hazardModel.id,
        // The `timeAwareFeatureId` is used to uniquely identify the feature on the map.
        // It is a combination of the hazard id and the updated date-time.
        // This is to ensure that the feature is updated on the map when the hazard id remains the same
        // but the hazard has been updated.
        // We add an index, if given, to allow for multiple features within a
        // single event to be differentiated between. Without, this was causing
        // warnings with multiple markers to only display a single marker.
        timeAwareFeatureId: `${hazardModel.id}-${updatedAt?.toMillis()}${index !== undefined && '-' + index.toString()}`,
        featureId: feature.id,
        entityType: hazardModel.entityType,
        entitySubType: hazardModel.entitySubType,
        eventId: hazardModel?.event,
        updatedAt: hazardModel['published-date-time'],
      },
    };
  }

  composeIncidentReportedNear(incidentModel: GetIncidentsModel): string {
    const streetName = incidentModel['location']?.value;
    const nearestCrossStreet = incidentModel['nearest-cross-street']?.value;
    const suburb = incidentModel['suburbs']?.at(0)?.toUpperCase();

    let content = '';

    if (streetName) {
      content = `${streetName.toUpperCase()}`;
    }

    if (streetName && nearestCrossStreet) {
      content += ' and ';
    }

    if (nearestCrossStreet) {
      content += `${nearestCrossStreet.toUpperCase()}`;
    }

    if (suburb && content?.length) {
      content += `, in ${suburb}`;
    }

    return content;
  }

  composeIncidentDescription(incidentModel: GetIncidentsModel): string {
    const suburb = incidentModel['suburbs']?.at(0)?.toUpperCase() ?? '';
    const lgas = incidentModel['lga']?.map((lga) => lga.toUpperCase()).join(', ') ?? '';

    let content = '';

    if (suburb) {
      content = `${suburb}`;
    }

    if (suburb && lgas) {
      content += ', ';
    }

    if (lgas) {
      content += `${lgas}`;
    }

    return content;
  }

  /**
   * Compose warning description
   * @param warningModel - The warning model
   */
  composeWarningDescription(warningModel: GetWarningsModel): string {
    const actionStatement = warningModel['action-statement']?.toUpperCase();
    const headline = warningModel['headline'];

    if (actionStatement?.length) {
      return `${actionStatement} - ${headline}`;
    }

    return headline ?? '';
  }
}
